// Central analytics helper.
//
// Fires a single event to BOTH Microsoft Clarity and Google Analytics (GA4)
// under the *same* logical name, so the two dashboards stay in sync. This is
// the one place event tracking lives — call trackEvent(...) instead of
// window.clarity directly so Clarity and GA can never drift apart.
//
// - Clarity is loaded by plugins/clarity.client.ts, which installs
//   window.clarity as a queue stub up front and fetches the tag on idle, so
//   calls made before it arrives are buffered. It receives the original,
//   human-readable event name unchanged.
// - GA is loaded by the nuxt-gtag module once NUXT_PUBLIC_GTAG_ID is set; it
//   creates window.dataLayer. Until an id is configured, the GA call no-ops.
//   GA4 requires event names to be snake_case, <=40 chars, made of letters/
//   numbers/underscores and starting with a letter — so for GA we send a
//   sanitized name and preserve the exact original string as `event_label`.
//
// Both calls are guarded and the whole thing is a no-op on the server, so it is
// always safe to call from any event handler.
//
// Dispatch is deferred to after the next paint (see `schedule` below). Almost
// every trackEvent() call is made from inside a click/keydown handler, and both
// vendors do real work synchronously on the call: gtag's dataLayer.push is the
// hook GA uses to build and send the hit, and Clarity's tag processes the event
// on the spot. That work lands inside the interaction, so it is counted in INP
// even though nothing on screen depends on it. Deferring it moves it out of the
// measured window without dropping a single event.

// GA's gtag() pushes the `arguments` object (not an array) onto dataLayer.
// Replicate that exactly (this is what nuxt-gtag does under the hood).
const gtag: (...args: any[]) => void = function () {
  ;(window as any).dataLayer?.push(arguments)
}

// Convert a human-readable Clarity event name into a GA4-safe event name.
//   "Download File (svg)"             -> "download_file_svg"
//   "prefetch.io Footer Logo Clicked" -> "prefetch_io_footer_logo_clicked"
//   "Change Palette View (Brand)"     -> "change_palette_view_brand"
export function toGa4EventName(name: string): string {
  let out = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_') // any run of non-alphanumerics -> single _
    .replace(/^_+|_+$/g, '') // trim leading/trailing underscores
  // GA4 event names must start with a letter.
  if (!/^[a-z]/.test(out)) out = 'e_' + out
  // GA4 hard limit is 40 characters (longer events are silently dropped).
  if (out.length > 40) out = out.slice(0, 40).replace(/_+$/, '')
  return out
}

// Events queued during the current interaction, flushed together once the
// browser has painted its result.
let pending: Array<() => void> = []
let scheduled = false
let unloadHooked = false

function flush() {
  scheduled = false
  const batch = pending
  pending = []
  for (const send of batch) {
    try {
      send()
    } catch {
      // A vendor that isn't loaded (or throws) must never break the page — and
      // must never take the rest of the batch down with it.
    }
  }
}

// requestAnimationFrame fires before the frame is painted, so the work is
// handed to a task queued from inside it: that runs after the paint the
// interaction is measured against. A visitor who leaves before the next frame
// (a link to another origin, a closed tab) would otherwise lose the events, so
// the page-dismissal signals flush the queue synchronously.
function schedule(send: () => void) {
  pending.push(send)

  if (!unloadHooked) {
    unloadHooked = true
    addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush()
    })
    addEventListener('pagehide', flush)
  }

  if (scheduled) return
  scheduled = true
  // A hidden tab is never painted and never gets an animation frame, so there
  // the events go out on the next task instead of waiting for one.
  if (typeof requestAnimationFrame === 'function' && document.visibilityState === 'visible') {
    requestAnimationFrame(() => setTimeout(flush, 0))
  } else {
    setTimeout(flush, 0)
  }
}

export function trackEvent(name: string, params?: Record<string, any>) {
  if (typeof window === 'undefined') return

  // The GA name is derived here rather than in the deferred callback: it is
  // cheap, and it keeps the queued closure to a plain vendor call.
  const ga4Name = toGa4EventName(name)

  schedule(() => {
    // --- Microsoft Clarity (original name, existing behaviour) ---
    try {
      ;(window as any).clarity?.('event', name)
    } catch {
      // Clarity not loaded yet — ignore.
    }

    // --- Google Analytics (GA4) ---
    // Sanitized name for GA's rules; the full original is kept as event_label so
    // nothing is lost and reports stay readable.
    try {
      gtag('event', ga4Name, { event_label: name, ...params })
    } catch {
      // GA not loaded yet — ignore.
    }
  })
}

// Attach a custom tag to the current Clarity session (and every recording,
// heatmap and filter that comes out of it).
//
// This is the one thing that has no GA counterpart, which is why it is its own
// function rather than a parameter on trackEvent: GA4 takes arbitrary params on
// each event, so context rides along with the event itself, while Clarity
// events are a bare name and nothing else. A tag is how the same context gets
// into Clarity — "show me the sessions where the visitor was on Firefox" is a
// filter over tags, not over events.
//
// Values must be a string (or an array of them); anything else is dropped by
// the tag. Like trackEvent, this is deferred out of the interaction, guarded,
// and a no-op on the server and before the tag has loaded (the queue stub in
// plugins/clarity.client.ts buffers it either way).
export function setClarityTag(key: string, value: string | string[]) {
  if (typeof window === 'undefined') return

  schedule(() => {
    try {
      ;(window as any).clarity?.('set', key, value)
    } catch {
      // Clarity not loaded yet — ignore.
    }
  })
}
